import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { arFollowupApi, claimsApi, dashboardApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  CurrencyInr, Warning, Clock, ArrowRight, Eye, FunnelSimple,
  MagnifyingGlass, ArrowsClockwise, WarningCircle, CheckCircle,
  CaretDown, CaretUp, FileText,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
const formatStatus = (s) => s?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '';

const ARFollowupPage = () => {
  const navigate = useNavigate();
  const [arData, setArData] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('days_old');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [arRes, followupsRes] = await Promise.all([
        dashboardApi.arAging(),
        arFollowupApi.list(),
      ]);
      setArData(arRes.data);
      setFollowups(followupsRes.data);
    } catch { toast.error('Failed to load AR data'); }
    finally { setLoading(false); }
  };

  const claims = arData?.claims || [];
  const buckets = arData?.aging_buckets || {};

  const filtered = claims
    .filter(c => {
      if (filter === 'breach' && !c.is_breach) return false;
      if (filter === '0-30' && c.days_old > 30) return false;
      if (filter === '31-60' && (c.days_old <= 30 || c.days_old > 60)) return false;
      if (filter === '61-90' && (c.days_old <= 60 || c.days_old > 90)) return false;
      if (filter === '90+' && c.days_old <= 90) return false;
      if (search && !c.claim_number?.toLowerCase().includes(search.toLowerCase()) &&
          !c.patient_name?.toLowerCase().includes(search.toLowerCase()) &&
          !c.payer_name?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const av = a[sortField] ?? 0;
      const bv = b[sortField] ?? 0;
      if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

  const SortHeader = ({ field, children }) => (
    <button className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold"
      onClick={() => { setSortField(field); setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }}>
      {children}
      {sortField === field && (sortDir === 'asc' ? <CaretUp className="h-3 w-3" /> : <CaretDown className="h-3 w-3" />)}
    </button>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500" /></div>;

  return (
    <div className="space-y-6" data-testid="ar-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']">AR & Collections</h1>
        <p className="text-sm text-slate-500 mt-1">Track outstanding cases, settlement breaches, and follow-ups</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="bg-white border border-slate-200 shadow-sm cursor-pointer hover:border-sky-300 transition-all" onClick={() => setFilter('all')}>
          <CardContent className="pt-3 pb-3 text-center">
            <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Total Outstanding</p>
            <p className="text-lg font-bold text-slate-800">{formatCurrency(arData?.total_outstanding)}</p>
            <p className="text-[10px] text-slate-400">{arData?.total_claims || 0} cases</p>
          </CardContent>
        </Card>
        <Card className={`border shadow-sm cursor-pointer hover:shadow-md transition-all ${arData?.breach_count > 0 ? 'bg-red-50 border-red-300' : 'bg-white border-slate-200'}`}
          onClick={() => setFilter('breach')}>
          <CardContent className="pt-3 pb-3 text-center">
            <p className="text-[10px] text-red-600 font-bold uppercase tracking-wider flex items-center justify-center gap-1">
              <WarningCircle className="h-3 w-3" /> Breached
            </p>
            <p className="text-lg font-bold text-red-700">{arData?.breach_count || 0}</p>
            <p className="text-[10px] text-red-400">{formatCurrency(arData?.breach_amount)}</p>
          </CardContent>
        </Card>
        {[
          { key: '0-30', label: '0-30 Days', color: 'text-emerald-700', border: 'hover:border-emerald-300' },
          { key: '31-60', label: '31-60 Days', color: 'text-amber-700', border: 'hover:border-amber-300' },
          { key: '61-90', label: '61-90 Days', color: 'text-orange-700', border: 'hover:border-orange-300' },
          { key: '90+', label: '90+ Days', color: 'text-red-700', border: 'hover:border-red-300' },
        ].map(b => (
          <Card key={b.key} className={`bg-white border border-slate-200 shadow-sm cursor-pointer ${b.border} transition-all`}
            onClick={() => setFilter(b.key)}>
            <CardContent className="pt-3 pb-3 text-center">
              <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">{b.label}</p>
              <p className={`text-lg font-bold ${b.color}`}>{formatCurrency(buckets[b.key]?.amount)}</p>
              <p className="text-[10px] text-slate-400">{buckets[b.key]?.count || 0} cases</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search by claim, patient, payer..." value={search}
            onChange={e => setSearch(e.target.value)} className="pl-9 border-slate-300" data-testid="ar-search" />
        </div>
        <Button variant="outline" size="sm" onClick={() => { setSearch(''); setFilter('all'); }} className="border-slate-300">
          <ArrowsClockwise className="h-4 w-4" />
        </Button>
        <FunnelSimple className="h-4 w-4 text-slate-400" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40 border-slate-300"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Claims</SelectItem>
            <SelectItem value="breach">Settlement Breached</SelectItem>
            <SelectItem value="0-30">0-30 Days</SelectItem>
            <SelectItem value="31-60">31-60 Days</SelectItem>
            <SelectItem value="61-90">61-90 Days</SelectItem>
            <SelectItem value="90+">90+ Days</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-400">{filtered.length} claim{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Claims Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="ar-claims-table">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-300" weight="duotone" />
              <p className="text-lg font-medium text-slate-700">All Clear!</p>
              <p className="text-sm text-slate-500 mt-1">No outstanding cases{filter !== 'all' ? ` in ${filter} bucket` : ''}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-slate-500"><SortHeader field="claim_number">Case No</SortHeader></TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Claim No</TableHead>
                    <TableHead className="text-slate-500"><SortHeader field="patient_name">Patient</SortHeader></TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Payer / TPA</TableHead>
                    <TableHead className="text-slate-500 text-right"><SortHeader field="outstanding">Outstanding</SortHeader></TableHead>
                    <TableHead className="text-slate-500 text-center"><SortHeader field="days_old">Age</SortHeader></TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Limit</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(c => (
                    <TableRow key={c.id}
                      className={`transition-colors cursor-pointer ${c.is_breach ? 'bg-red-50/70 hover:bg-red-100/70' : 'hover:bg-sky-50/50'}`}
                      onClick={() => navigate(`/claims/${c.id}`)}
                      data-testid={`ar-row-${c.id}`}>
                      <TableCell>
                        <span className={`font-semibold text-sm ${c.is_breach ? 'text-red-700' : 'text-sky-700'}`}>{c.claim_number}</span>
                      </TableCell>
                      <TableCell className="text-xs font-mono text-orange-600">{c.insurance_claim_no || '-'}</TableCell>
                      <TableCell>
                        <span className={`text-sm font-medium ${c.is_breach ? 'text-red-800' : 'text-slate-800'}`}>{c.patient_name}</span>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{c.payer_name || '-'}</TableCell>
                      <TableCell className="text-right">
                        <span className={`font-semibold text-sm ${c.is_breach ? 'text-red-700' : 'text-slate-800'}`}>{formatCurrency(c.outstanding)}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                          c.is_breach ? 'bg-red-200 text-red-800' :
                          c.days_old > 60 ? 'bg-orange-100 text-orange-700' :
                          c.days_old > 30 ? 'bg-amber-100 text-amber-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>
                          {c.is_breach && <WarningCircle className="h-3 w-3" />}
                          {c.days_old}d
                        </span>
                      </TableCell>
                      <TableCell className="text-center text-xs text-slate-500">{c.settlement_days}d</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`border-0 text-[10px] ${
                          c.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                          c.status === 'payment_pending' ? 'bg-violet-100 text-violet-700' :
                          c.status === 'query_raised' ? 'bg-yellow-100 text-yellow-700' :
                          c.status === 'partially_settled' ? 'bg-cyan-100 text-cyan-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>{formatStatus(c.status)}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="hover:bg-sky-50 text-sky-600 h-7 w-7 p-0"
                          onClick={e => { e.stopPropagation(); navigate(`/claims/${c.id}`); }}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Follow-ups */}
      {followups.length > 0 && (
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-['Outfit'] text-slate-800">Recent Follow-ups</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {followups.slice(0, 10).map(f => (
                <div key={f.id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 hover:bg-sky-50 cursor-pointer transition-colors"
                  onClick={() => f.claim_id && navigate(`/claims/${f.claim_id}`)}>
                  <div className="h-8 w-8 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
                    <FileText className="h-4 w-4 text-sky-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">{f.claim_number}</span>
                      <span className="text-[10px] text-slate-400">{f.patient_name}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">{f.notes || f.action || '-'}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">by {f.created_by_name} • {new Date(f.created_at).toLocaleDateString('en-IN')}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-300 shrink-0 mt-1" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ARFollowupPage;
